import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Trash2, Check, LogOut, Plus, Send, ChevronLeft, ChevronRight } from "lucide-react";
import { api, apiError, formatDateFr } from "@/lib/api";
import { RedButton, GhostButton } from "@/components/Layout";
import AdminAgenda from "@/components/AdminAgenda";
import AdminSchedule from "@/components/AdminSchedule";

const TABS = ["Planning", "Gestion du planning", "Demandes", "Prestations", "Réglages"];

const STATUS_FR = { pending: "EN ATTENTE", confirmed: "CONFIRMÉ", done: "TERMINÉ", cancelled: "ANNULÉ" };


const EMPTY_SERVICE = {
  name: "", price_label: "", duration_days: 0.5, category: "reprogrammation", bookable: true,
};

const Input = ({ label, ...props }) => (
  <label className="block">
    <span className="font-mono-tech text-[10px] tracking-[0.2em] text-zinc-400 uppercase">{label}</span>
    <input className="mt-2 w-full bg-black border border-zinc-800 px-3 py-2.5 text-white focus:border-sared focus:outline-none" {...props} />
  </label>
);

export default function Admin() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState("Planning");
  const [services, setServices] = useState([]);
  const [settings, setSettings] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [requests, setRequests] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [newService, setNewService] = useState(EMPTY_SERVICE);
  const [candidates, setCandidates] = useState([]);
  const [invites, setInvites] = useState([]);
  const [manualInvite, setManualInvite] = useState({ name: "", email: "" });
  const [newImg, setNewImg] = useState({ url: "", category: "Réparation", caption: "", order: 99 });
  const [uploadCat, setUploadCat] = useState("Réparation");
  const [uploading, setUploading] = useState(false);

  const uploadImages = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    let ok = 0;
    for (const f of Array.from(files)) {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("category", uploadCat);
      fd.append("order", 99);
      try {
        await api.post("/admin/gallery/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        ok += 1;
      } catch (e) { toast.error(`${f.name} : ${apiError(e)}`); }
    }
    setUploading(false);
    if (ok > 0) { toast.success(`${ok} photo(s) ajoutée(s)`); loadAll(); }
  };

  const moveImage = async (item, dir) => {
    const list = gallery.filter((g) => g.category === item.category);
    const i = list.findIndex((g) => g.id === item.id);
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    try {
      await Promise.all([
        api.patch(`/admin/gallery/${item.id}`, { order: list[j].order ?? j }),
        api.patch(`/admin/gallery/${list[j].id}`, { order: item.order ?? i }),
      ]);
      loadAll();
    } catch (e) { toast.error(apiError(e)); }
  };

  const loadAll = useCallback(async () => {
    try {
      const [sv, st, rv, gl, rc, ri, ar, ap] = await Promise.all([
        api.get("/services"),
        api.get("/settings"), api.get("/admin/reviews"), api.get("/gallery"),
        api.get("/admin/review-candidates"), api.get("/admin/review-invites"),
        api.get("/admin/appointment-requests"), api.get("/admin/appointments"),
      ]);
      setServices(sv.data);
      setSettings(st.data); setReviews(rv.data); setGallery(gl.data);
      setCandidates(rc.data); setInvites(ri.data); setRequests(ar.data);
      setBookings(ap.data.filter((a) => a.source === "client").sort((a, b) => (
        (a.status === "pending" ? 0 : 1) - (b.status === "pending" ? 0 : 1)
        || (b.created_at || "").localeCompare(a.created_at || ""))));
    } catch (e) {
      toast.error(apiError(e));
    }
  }, []);

  useEffect(() => {
    api.get("/auth/me").then((r) => { setMe(r.data); loadAll(); })
      .catch(() => nav("/admin/login"));
  }, [nav, loadAll]);

  const setBookingStatus = async (reference, status) => {
    if (status === "cancelled" && !window.confirm(`Annuler le rendez-vous ${reference} ?`)) return;
    try {
      await api.patch(`/admin/appointments/${reference}`, { status });
      toast.success(status === "confirmed" ? "Rendez-vous validé" : "Rendez-vous annulé");
      loadAll();
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveSettings = async () => {    try {
      const { data } = await api.put("/admin/settings", settings);
      setSettings(data);
      toast.success("Réglages enregistrés");
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveService = async (sv) => {
    try {
      await api.put(`/admin/services/${sv.slug}`, {
        name: sv.name, price_label: sv.price_label, duration_days: sv.duration_days ?? 0.5, image: sv.image || "",
      });
      toast.success(`${sv.name} mis à jour`);
      loadAll();
    } catch (e) { toast.error(apiError(e)); }
  };


  const createService = async () => {
    if (!newService.name.trim() || !newService.price_label.trim()) {
      toast.error("Nom et tarif requis");
      return;
    }
    try {
      await api.post("/admin/services", { ...newService, duration_days: Number(newService.duration_days) });
      toast.success("Prestation ajoutée");
      setNewService(EMPTY_SERVICE);
      loadAll();
    } catch (e) { toast.error(apiError(e)); }
  };

  const deleteService = async (sv) => {
    if (!window.confirm(`Supprimer la prestation « ${sv.name} » ?`)) return;
    try {
      await api.delete(`/admin/services/${sv.slug}`);
      toast.success("Prestation supprimée");
      loadAll();
    } catch (e) { toast.error(apiError(e)); }
  };

  const invite = async (payload, manual = false) => {
    if (!payload.email || !payload.name) {
      toast.error("Nom et e-mail requis");
      return;
    }
    try {
      await api.post("/admin/review-invites", payload);
      toast.success(`Invitation envoyée à ${payload.name}`);
      if (manual) setManualInvite({ name: "", email: "" });
      loadAll();
    } catch (e) { toast.error(apiError(e)); }
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    localStorage.removeItem("sa_token");
    nav("/admin/login");
  };

  if (!me || !settings) {
    return <div className="min-h-screen bg-black flex items-center justify-center"><Loader2 className="animate-spin text-sared" /></div>;
  }

  return (
    <div className="min-h-screen bg-black" data-testid="admin-page">
      <header className="border-b border-sared bg-black sticky top-0 z-40">
        <div className="mx-auto max-w-[1400px] px-4 md:px-8 h-[70px] flex items-center justify-between gap-4">
          <div>
            <div className="font-head text-2xl text-white">SHRAL<span className="text-sared">’</span>AUTO — Gestion</div>
            <div className="font-mono-tech text-[9px] tracking-[0.2em] text-zinc-600">{me.email}</div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="font-mono-tech text-[10px] tracking-[0.18em] text-zinc-400 hover:text-sared">VOIR LE SITE</Link>
            <button onClick={logout} className="p-3 border border-zinc-800 text-zinc-300 hover:border-sared" data-testid="admin-logout"><LogOut size={16} /></button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[1400px] px-4 md:px-8 py-8">
        <div className="flex flex-wrap gap-2 mb-8" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t.toLowerCase().replace(/[^a-z]/g, "")}`}
                    className={`font-head text-sm uppercase tracking-wider px-4 py-2.5 border ${
                      tab === t ? "bg-sared border-sared text-white" : "border-zinc-800 text-zinc-300 hover:border-sared"}`}>
              {t}
            </button>
          ))}
        </div>

        {tab === "Planning" && <AdminAgenda />}
        {tab === "Gestion du planning" && <AdminSchedule />}

        {tab === "Demandes" && (
          <div data-testid="admin-requests-tab">
          <div className="grid gap-5 md:grid-cols-2 mb-10" data-testid="admin-online-bookings">
            {bookings.map((r) => (
              <div key={r.reference} className="border border-zinc-800 bg-[#0a0a0a] p-6" data-testid={`admin-booking-${r.reference}`}>
                <div className="flex justify-between items-center">
                  <span className="font-mono-tech text-xs text-sared">{r.reference}</span>
                  <span className={`font-mono-tech text-[10px] tracking-[0.16em] px-2 py-1 border ${
                    r.status === "pending" ? "border-amber-500/70 bg-amber-50 text-amber-800"
                    : r.status === "cancelled" ? "border-zinc-300 text-zinc-500"
                    : "border-emerald-600/70 bg-emerald-50 text-emerald-800"}`}>
                    {STATUS_FR[r.status] || r.status}
                  </span>
                </div>
                <div className="mt-3 font-head text-xl text-white uppercase">{r.service_text}</div>
                <div className="mt-2 text-sm text-zinc-300 capitalize">
                  {formatDateFr(r.date)} · {r.start} – {r.end}
                </div>
                <div className="mt-3 text-sm text-zinc-400">
                  {[r.first_name, r.last_name].filter(Boolean).join(" ")} • {r.phone}{r.email ? ` • ${r.email}` : ""}
                </div>
                {(r.vehicle_brand || r.plate) && (
                  <div className="mt-1 text-sm text-zinc-500">{r.vehicle_brand} {r.vehicle_model} {r.plate ? `• ${r.plate}` : ""}</div>
                )}
                {r.description && <p className="mt-3 text-sm text-zinc-300 whitespace-pre-line">{r.description}</p>}
                <div className="mt-5 flex flex-wrap items-center gap-5">
                  <a href={`tel:${String(r.phone || "").replace(/\s/g, "")}`}
                     className="font-mono-tech text-[10px] tracking-[0.18em] text-sared hover:text-white">APPELER</a>
                  {r.status === "pending" && (
                    <button data-testid={`booking-accept-${r.reference}`} onClick={() => setBookingStatus(r.reference, "confirmed")}
                            className="font-mono-tech text-[10px] tracking-[0.18em] text-emerald-700 hover:text-emerald-900">VALIDER</button>
                  )}
                  {r.status !== "cancelled" && (
                    <button data-testid={`booking-cancel-${r.reference}`} onClick={() => setBookingStatus(r.reference, "cancelled")}
                            className="font-mono-tech text-[10px] tracking-[0.18em] text-zinc-500 hover:text-sared">ANNULER</button>
                  )}
                </div>
              </div>
            ))}
            {bookings.length === 0 && <p className="text-zinc-600" data-testid="no-online-bookings">Aucune réservation en ligne</p>}
          </div>

          {requests.length > 0 && (
          <div className="grid gap-5 md:grid-cols-2" data-testid="admin-appointment-requests">
            {requests.map((r) => (
              <div key={r.reference} className="border border-zinc-800 bg-[#0a0a0a] p-6" data-testid={`admin-request-${r.reference}`}>
                <div className="flex justify-between">
                  <span className="font-mono-tech text-xs text-sared">{r.reference}</span>
                  <span className="font-mono-tech text-[10px] text-zinc-600">{(r.created_at || "").slice(0, 10)}</span>
                </div>
                <div className="mt-3 font-head text-xl text-white uppercase">{(r.categories || []).join(" • ")}</div>
                <div className="mt-3 text-sm text-zinc-400">{r.first_name} {r.last_name} • {r.phone} • {r.email}</div>
                {(r.vehicle_brand || r.plate) && (
                  <div className="mt-1 text-sm text-zinc-500">
                    {r.vehicle_brand} {r.vehicle_model} {r.vehicle_year} {r.plate ? `• ${r.plate}` : ""}
                  </div>
                )}
                {r.message && <p className="mt-4 text-sm text-zinc-300 whitespace-pre-line">{r.message}</p>}
                <div className="mt-5 flex items-center gap-5">
                  <a href={`tel:${String(r.phone || "").replace(/\s/g, "")}`}
                     className="font-mono-tech text-[10px] tracking-[0.18em] text-sared hover:text-white">APPELER</a>
                  <button data-testid={`request-delete-${r.reference}`}
                          onClick={async () => {
                            if (!window.confirm(`Supprimer la demande ${r.reference} ?`)) return;
                            try { await api.delete(`/admin/appointment-requests/${r.reference}`); toast.success("Demande supprimée"); loadAll(); }
                            catch (e) { toast.error(apiError(e)); }
                          }}
                          className="font-mono-tech text-[10px] tracking-[0.18em] text-zinc-500 hover:text-sared">SUPPRIMER</button>
                </div>
              </div>
            ))}
            {requests.length === 0 && <p className="text-zinc-600">Aucune demande de rendez-vous</p>}
          </div>
          )}
          </div>
        )}

        {tab === "Prestations" && (
          <div className="grid gap-4" data-testid="admin-services">
            <div className="border border-sared bg-[#0a0a0a] p-5 grid gap-4 md:grid-cols-[2fr,1fr,1fr,1fr,auto] items-end" data-testid="admin-new-service">
              <Input label="Nouvelle prestation" value={newService.name} data-testid="new-svc-name"
                     onChange={(e) => setNewService({ ...newService, name: e.target.value })} />
              <Input label="Tarif" value={newService.price_label} data-testid="new-svc-price"
                     placeholder="Ex. : 90 €"
                     onChange={(e) => setNewService({ ...newService, price_label: e.target.value })} />
              <label className="block">
                <span className="font-mono-tech text-[10px] tracking-[0.2em] text-zinc-400 uppercase">Catégorie</span>
                <select value={newService.category} data-testid="new-svc-category"
                        onChange={(e) => setNewService({ ...newService, category: e.target.value })}
                        className="mt-2 w-full bg-black border border-zinc-800 px-3 py-2.5 text-white focus:border-sared focus:outline-none">
                  <option value="reprogrammation">Reprogrammation</option>
                </select>
              </label>
              <RedButton className="px-4 py-2.5 text-sm" onClick={createService} data-testid="new-svc-add">
                <Plus size={16} className="inline" /> Ajouter
              </RedButton>
            </div>

            {services.map((sv, idx) => (
              <div key={sv.slug} className="border border-zinc-800 bg-[#0a0a0a] p-5 grid gap-4 md:grid-cols-[2fr,1fr,1fr,auto,auto] items-end">
                <Input label="Nom" value={sv.name} data-testid={`svc-name-${sv.slug}`}
                       onChange={(e) => setServices(services.map((x, i) => i === idx ? { ...x, name: e.target.value } : x))} />
                <Input label="Tarif" value={sv.price_label} data-testid={`svc-price-${sv.slug}`}
                       onChange={(e) => setServices(services.map((x, i) => i === idx ? { ...x, price_label: e.target.value } : x))} />
                <RedButton className="px-4 py-2.5 text-sm" onClick={() => saveService(services[idx])} data-testid={`svc-save-${sv.slug}`}>Enregistrer</RedButton>
                <button onClick={() => deleteService(sv)} data-testid={`svc-delete-${sv.slug}`}
                        className="px-3 py-2.5 border border-zinc-700 text-zinc-400 hover:border-sared hover:text-sared">
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        )}

        {tab === "Réglages" && (
          <div className="border border-zinc-800 bg-[#0a0a0a] p-6 md:p-8 grid gap-6 md:grid-cols-2" data-testid="admin-settings">
            {[["phone", "Téléphone"], ["hours", "Horaires"], ["email", "E-mail"],
              ["slogan", "Slogan"], ["tagline", "Baseline"], ["intro", "Présentation"], ["promo", "Bandeau promo"],
              ["tarifs_note", "Note tarifs"], ["repro_legal", "Mention réglementaire repro"],
              ["social_instagram", "Instagram (URL)"], ["social_facebook", "Facebook (URL)"], ["social_snapchat", "Snapchat (URL)"]].map(([k, label]) => (
              <Input key={k} label={label} value={settings[k] ?? ""} data-testid={`setting-${k}`}
                     onChange={(e) => setSettings({ ...settings, [k]: e.target.value })} />
            ))}
            <div className="md:col-span-2">
              <RedButton onClick={saveSettings} data-testid="save-settings">Enregistrer les réglages</RedButton>
            </div>
          </div>
        )}

      </div>
      <div className="h-10" />
    </div>
  );
}
